'use client';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  type CrmTriggerJob,
  type CrmTriggersStatus,
  type OnboardingSignal,
  crmTriggerJobLabels,
  crmTriggerJobValues,
  onboardingSignalLabels,
  onboardingSignalValues,
} from '@borradh-workspace/api-client/types';
import { Play } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import {
  type CrmTriggerRequest,
  useCrmTriggersStatus,
  useRunCrmTrigger,
} from '../../api/crm-triggers';
import { OrgCombobox, type SelectedOrg } from '../org-combobox';

/** What each scheduled job does, and how `now` changes what it finds. */
const JOB_NOTES: Record<CrmTriggerJob, string> = {
  'clinic-health':
    'Scores every clinic 0–100 (runs nightly at 06:00 UTC), writes the band and reasons to Twenty, and DMs a CSM whose clinic dropped a band or 15 points in a week. The managers’ channel gets the summary.',
  'onboarding-sync':
    'Reads each onboarding clinic’s platform signals (data received, Stripe, Meta), ticks the checklist in Twenty, moves the stage forward and writes what is missing (runs hourly). Alerts to Eoin, Senan and Daniel go out only in Irish working hours, Monday to Friday: set “Run as of” to a weekday 09:00 Irish time to see them.',
  'onboarding-digest':
    'One DM to Eoin listing every clinic at Onboarding with its stage, days since signing against 21, blocker and what is missing (runs Monday 08:00 Dublin). Sends whenever fired; “Run as of” only changes the day counts.',
  'demo-reminders':
    'DMs the rep whose demo starts in 10–20 minutes (runs every 5 minutes). Set “Run as of” to 15 minutes before a booked demo to see one.',
  'contract-nudge':
    'DMs the rep whose contract has sat unsigned for 2 days, up to 30 days back (runs hourly). Each nudge is sent once; a second run says already_sent.',
};

const DEP_LABELS: Record<keyof CrmTriggersStatus['deps'], string> = {
  twenty: 'Twenty',
  granola: 'Granola',
  slackBot: 'Slack bot',
  managersWebhook: 'Managers channel',
  posthogUsage: 'PostHog usage',
  docuseal: 'DocuSeal',
};

/** A datetime-local value → ISO instant, or undefined when the field is empty. */
const toIso = (local: string): string | undefined => {
  if (!local) return undefined;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const pretty = (value: unknown) => JSON.stringify(value, null, 2);

/**
 * The HTTP status behind a failed request. The client's `ApiFetchError` and
 * `ApiClientError` carry it as `status`; a raw HTTP error carries it on its
 * `response`.
 */
const httpStatus = (error: unknown): number | undefined => {
  const e = error as
    | { status?: unknown; response?: { status?: number } }
    | null
    | undefined;
  return typeof e?.status === 'number' ? e.status : e?.response?.status;
};

/** What the page shows for a failed run: status, code, message and details, as the API sent them. */
const errorBody = (error: unknown) => {
  const e = error as {
    message?: string;
    code?: string;
    details?: Record<string, unknown>;
  } | null;
  return {
    status: httpStatus(error),
    code: e?.code,
    message: error instanceof Error ? error.message : String(error),
    details: e?.details,
  };
};

interface RunOutcome {
  at: string;
  ms: number;
  ok: boolean;
  body: unknown;
}

/**
 * One trigger: its inputs, a Run button, and the JSON the API answered with,
 * kept on the card so several triggers can be compared side by side.
 */
function TriggerCard({
  title,
  description,
  children,
  request,
  invalid,
}: {
  title: string;
  description: string;
  children?: ReactNode;
  /** The request to send; null when the inputs are not complete yet. */
  request: () => CrmTriggerRequest | null;
  /** Why the request cannot be sent yet, shown instead of running. */
  invalid?: string;
}) {
  const { run, isRunning } = useRunCrmTrigger();
  const [outcome, setOutcome] = useState<RunOutcome | null>(null);

  const onRun = async () => {
    const req = request();
    if (!req) return;
    const started = Date.now();
    try {
      const body = await run(req);
      setOutcome({
        at: new Date().toISOString(),
        ms: Date.now() - started,
        ok: true,
        body,
      });
    } catch (error) {
      setOutcome({
        at: new Date().toISOString(),
        ms: Date.now() - started,
        ok: false,
        body: errorBody(error),
      });
    }
  };

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        {children}
        {outcome && (
          <section
            aria-label={`${title} result`}
            className="flex flex-col gap-2"
          >
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant={outcome.ok ? 'secondary' : 'destructive'}>
                {outcome.ok ? 'OK' : 'Failed'}
              </Badge>
              <span>
                {new Date(outcome.at).toLocaleTimeString('en-GB')} ·{' '}
                {outcome.ms} ms
              </span>
            </div>
            <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
              {pretty(outcome.body)}
            </pre>
          </section>
        )}
      </CardContent>
      <CardFooter className="flex items-center justify-between gap-3">
        <span className="text-xs text-destructive">{invalid}</span>
        <Button
          type="button"
          onClick={onRun}
          disabled={isRunning || !!invalid}
          aria-label={`Run ${title}`}
        >
          <Play />
          {isRunning ? 'Running…' : 'Run'}
        </Button>
      </CardFooter>
    </Card>
  );
}

function JobCard({ job }: { job: CrmTriggerJob }) {
  const id = useId();
  const [asOf, setAsOf] = useState('');
  const bad = asOf !== '' && toIso(asOf) === undefined;
  return (
    <TriggerCard
      title={crmTriggerJobLabels[job]}
      description={JOB_NOTES[job]}
      invalid={bad ? 'That date is not valid' : undefined}
      request={() => ({
        kind: 'job',
        job,
        input: { now: toIso(asOf) },
      })}
    >
      <div className="grid gap-1.5">
        <Label htmlFor={id}>Run as of (optional)</Label>
        <Input
          id={id}
          type="datetime-local"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
        />
      </div>
    </TriggerCard>
  );
}

function GranolaImportCard() {
  const id = useId();
  const [noteId, setNoteId] = useState('');
  const [inline, setInline] = useState(true);
  return (
    <TriggerCard
      title="Re-import a Granola note"
      description="Fetches one call note from Granola again and writes it to the attendee’s clinic or opportunity in Twenty, as the webhook and the 5-minute poll do. Inline runs it here and shows the outcome; otherwise it is queued for the worker as a manual import."
      invalid={noteId.trim() ? undefined : 'Enter a Granola note id'}
      request={() => ({
        kind: 'granola-import',
        input: { granolaNoteId: noteId.trim(), inline },
      })}
    >
      <div className="grid gap-1.5">
        <Label htmlFor={id}>Granola note id</Label>
        <Input
          id={id}
          value={noteId}
          onChange={(e) => setNoteId(e.target.value)}
          placeholder="e.g. 7f3c…"
        />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${id}-inline`}
          checked={inline}
          onCheckedChange={(v) => setInline(v === true)}
        />
        <Label htmlFor={`${id}-inline`}>Run inline and show the outcome</Label>
      </div>
    </TriggerCard>
  );
}

function OnboardingSignalCard() {
  const id = useId();
  const [org, setOrg] = useState<SelectedOrg | null>(null);
  const [companyId, setCompanyId] = useState('');
  const [signal, setSignal] = useState<OnboardingSignal>('meta_connected');
  const [on, setOn] = useState(true);
  const clinicMissing = !org && !companyId.trim();
  return (
    <TriggerCard
      title="Simulate an onboarding signal"
      description="Makes Meta connected, Stripe payments or data received read as on or off for one clinic by writing the rows the hourly onboarding sync reads (a placeholder Meta row, a Stripe Connect row with charges enabled, a marked import run). Then run “Onboarding sync and alerts” to see the checklist tick in Twenty. Preview database only; “off” removes only what this page added."
      invalid={
        clinicMissing ? 'Pick a clinic or enter a Twenty company id' : undefined
      }
      request={() => ({
        kind: 'onboarding-signal',
        input: {
          ...(org
            ? { organizationId: org.id }
            : { twentyCompanyId: companyId.trim() }),
          signal,
          on,
        },
      })}
    >
      <div className="grid gap-1.5">
        <Label>Clinic (Borradh organization)</Label>
        <OrgCombobox value={org} onChange={setOrg} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-company`}>Or a Twenty company id</Label>
        <Input
          id={`${id}-company`}
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          placeholder="Resolved through the company’s borradhOrgId"
          disabled={!!org}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-signal`}>Signal</Label>
        <Select
          value={signal}
          onValueChange={(v) => setSignal(v as OnboardingSignal)}
        >
          <SelectTrigger id={`${id}-signal`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {onboardingSignalValues.map((value) => (
              <SelectItem key={value} value={value}>
                {onboardingSignalLabels[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Switch id={`${id}-on`} checked={on} onCheckedChange={setOn} />
        <Label htmlFor={`${id}-on`}>
          {on ? 'Mark as present' : 'Mark as absent'}
        </Label>
      </div>
    </TriggerCard>
  );
}

function SlackDmCard() {
  const id = useId();
  const [email, setEmail] = useState('');
  const [text, setText] = useState('Test DM from the CRM trigger page.');
  const valid = /\S+@\S+\.\S+/.test(email.trim()) && text.trim().length > 0;
  return (
    <TriggerCard
      title="Slack DM a teammate"
      description="One message from the Borradh CRM Slack app to a teammate, found by their work email, through the same notifier every CRM alert uses. not_in_slack means nobody in Slack has that email."
      invalid={valid ? undefined : 'Enter a work email and a message'}
      request={() => ({
        kind: 'slack-dm',
        input: { email: email.trim(), text: text.trim() },
      })}
    >
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-email`}>Teammate email</Label>
        <Input
          id={`${id}-email`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@borradh.io"
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-text`}>Message</Label>
        <Textarea
          id={`${id}-text`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
        />
      </div>
    </TriggerCard>
  );
}

function StatusBar({
  status,
  isLoading,
  error,
}: {
  status: CrmTriggersStatus | undefined;
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading)
    return <Skeleton className="h-10 w-full" aria-label="Loading status" />;
  if (error)
    return (
      <Alert variant="destructive">
        <AlertTitle>
          {httpStatus(error) === 404
            ? 'Not available here'
            : 'Could not read the host status'}
        </AlertTitle>
        <AlertDescription>
          {httpStatus(error) === 404
            ? 'The CRM triggers exist only on PR previews and local machines; this API answered 404.'
            : error.message}
        </AlertDescription>
      </Alert>
    );
  if (!status) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-2 text-sm"
      aria-label="Host dependencies"
    >
      <span className="text-muted-foreground">This host has:</span>
      {(Object.keys(DEP_LABELS) as (keyof CrmTriggersStatus['deps'])[]).map(
        (dep) => (
          <Badge key={dep} variant={status.deps[dep] ? 'default' : 'outline'}>
            {DEP_LABELS[dep]}: {status.deps[dep] ? 'yes' : 'no'}
          </Badge>
        )
      )}
      <span className="text-xs text-muted-foreground">
        A job whose dependency is missing answers <code>skipped</code>.
      </span>
    </div>
  );
}

/** The preview-only CRM trigger page: every CRM automation, fired by hand. */
export function CrmTriggersPage() {
  const { data: status, isLoading, error } = useCrmTriggersStatus();
  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">CRM triggers</h1>
        <p className="text-sm text-muted-foreground">
          Fire each CRM automation now instead of waiting for its schedule.
          Every run answers with the job’s own result. Jobs read and write the
          real Twenty workspace this host is pointed at and DM real teammates in
          Slack; the simulated signals write only this preview’s database.
        </p>
      </header>
      <StatusBar status={status} isLoading={isLoading} error={error} />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {crmTriggerJobValues.map((job) => (
          <JobCard key={job} job={job} />
        ))}
        <GranolaImportCard />
        <OnboardingSignalCard />
        <SlackDmCard />
      </div>
    </div>
  );
}
